import React from "react";
import { useRoutes } from "react-router-dom";
import AuthLayout from "../views/layouts/AuthLayout";
import MainLayout from "../views/layouts/MainLayout";
import { CONSTANT } from "./Constant";

const PageNotFound = React.lazy(
  () => import("../views/error_pages/PageNotFound"),
);
const Dashboard = React.lazy(() => import("../views/app_pages/Dashboard"));
const Registration = React.lazy(
  () => import("../views/app_pages/Auth/Registration"),
);
const Login = React.lazy(() => import("../views/app_pages/Auth/Login"));
const ForgotPassword = React.lazy(
  () => import("../views/app_pages/Auth/ForgotPassword"),
);
const MyProfile = React.lazy(() => import("../views/app_pages/MyProfile"));
const User = React.lazy(() => import("../views/app_pages/User"));
const Role = React.lazy(() => import("../views/app_pages/Role"));
const RoleList = React.lazy(
  () => import("../views/app_pages/Role/components/ListComponent"),
);
const RoleAdd = React.lazy(
  () => import("../views/app_pages/Role/components/AddComponent"),
);
const RoleEdit = React.lazy(
  () => import("../views/app_pages/Role/components/EditComponent"),
);

const Brand = React.lazy(() => import("../views/app_pages/Brand"));
const Transporter = React.lazy(() => import("../views/app_pages/Transporter"));
const DiscountScheme = React.lazy(
  () => import("../views/app_pages/DiscountScheme"),
);
const DiscountSchemeList = React.lazy(
  () => import("../views/app_pages/DiscountScheme/components/ListComponent"),
);
const DiscountSchemeAdd = React.lazy(
  () => import("../views/app_pages/DiscountScheme/components/AddComponent"),
);
const DiscountSchemeEdit = React.lazy(
  () => import("../views/app_pages/DiscountScheme/components/EditComponent"),
);
const UnitOfMeasurement = React.lazy(
  () => import("../views/app_pages/UnitOfMeasurement"),
);
const UQCMaster = React.lazy(
  () => import("../views/app_pages/UQCMaster"),
);
const ItemDefaultBin = React.lazy(
  () => import("../views/app_pages/ItemDefaultBin"),
);

const Warehouse = React.lazy(() => import("../views/app_pages/Warehouse"));
const Location = React.lazy(() => import("../views/app_pages/Location"));
const ItemCategory = React.lazy(
  () => import("../views/app_pages/ItemCategory"),
);
const CustomerCategory = React.lazy(
  () => import("../views/app_pages/CustomerCategory"),
);
const CustomerAccountType = React.lazy(
  () => import("../views/app_pages/CustomerAccountType"),
);
const ItemSegment = React.lazy(() => import("../views/app_pages/ItemSegment"));
const ProductGroupMaster = React.lazy(() => import("../views/app_pages/ProductGroupMaster"));
const Currency = React.lazy(() => import("../views/app_pages/Currency"));
const PurchaseOrder = React.lazy(
  () => import("../views/app_pages/Procurement/PurchaseOrder"),
);
const PurchaseOrderList = React.lazy(
  () =>
    import("../views/app_pages/Procurement/PurchaseOrder/components/ListComponent"),
);
const PurchaseOrderAdd = React.lazy(
  () =>
    import("../views/app_pages/Procurement/PurchaseOrder/components/AddComponent"),
);
const PurchaseOrderEdit = React.lazy(
  () =>
    import("../views/app_pages/Procurement/PurchaseOrder/components/EditComponent"),
);

const PurchaseReceive = React.lazy(
  () => import("../views/app_pages/Procurement/PurchaseReceive"),
);
const PurchaseReceiveList = React.lazy(
  () =>
    import("../views/app_pages/Procurement/PurchaseReceive/components/ListComponent"),
);
const PurchaseReceiveAdd = React.lazy(
  () =>
    import("../views/app_pages/Procurement/PurchaseReceive/components/AddComponent"),
);
const QRVerificationComponent = React.lazy(
  () =>
    import("../views/app_pages/Procurement/PurchaseReceive/components/QRVerificationComponent"),
);
const PurchaseReceiveReceive = React.lazy(
  () =>
    import("../views/app_pages/Procurement/PurchaseReceive/components/ReceiveComponent"),
);
const PurchaseInvoiceReceive = React.lazy(
  () =>
    import("../views/app_pages/Procurement/PurchaseReceive/components/InvoiceReceiveComponent"),
);
const PurchaseInvoiceReceiving = React.lazy(
  () =>
    import("../views/app_pages/Procurement/PurchaseReceive/components/InvoiceReceivingComponent"),
);
const PurchaseInvoiceVerification = React.lazy(
  () =>
    import("../views/app_pages/Procurement/PurchaseReceive/components/InvoiceVerificationComponent"),
);
const ReceiveSessionStart = React.lazy(
  () =>
    import("../views/app_pages/Procurement/PurchaseReceive/ReceiveSession/SessionStartComponent"),
);
const ReceiveSessionScan = React.lazy(
  () =>
    import("../views/app_pages/Procurement/PurchaseReceive/ReceiveSession/BatchReceiveComponent"),
);
const ReceiveSessionFinalize = React.lazy(
  () =>
    import("../views/app_pages/Procurement/PurchaseReceive/ReceiveSession/SessionFinalizeComponent"),
);
const Quotation = React.lazy(() => import("../views/app_pages/Sales/Quotation"));
const QuotationList = React.lazy(
  () => import("../views/app_pages/Sales/Quotation/components/ListComponent"),
);
const QuotationAdd = React.lazy(
  () => import("../views/app_pages/Sales/Quotation/components/AddComponent"),
);
const QuotationEdit = React.lazy(
  () => import("../views/app_pages/Sales/Quotation/components/EditComponent"),
);
const SalesOrder = React.lazy(
  () => import("../views/app_pages/Sales/SalesOrder"),
);
const SalesOrderList = React.lazy(
  () => import("../views/app_pages/Sales/SalesOrder/components/ListComponent"),
);
const SalesOrderAdd = React.lazy(
  () => import("../views/app_pages/Sales/SalesOrder/components/AddComponent"),
);
const CounterSale = React.lazy(
  () => import("../views/app_pages/Sales/CounterSale"),
);
const CounterSaleList = React.lazy(
  () => import("../views/app_pages/Sales/CounterSale/components/ListComponent"),
);
const CounterSaleAdd = React.lazy(
  () => import("../views/app_pages/Sales/CounterSale/components/AddComponent"),
);
const CounterSaleEdit = React.lazy(
  () => import("../views/app_pages/Sales/CounterSale/components/EditComponent"),
);
// POS form — keyboard-driven Counter Sale entry. Lives alongside the
// classic CounterSaleAdd; both forms coexist until the POS is validated
// in production. See `Sales/CounterSale/components/PosForm/`.
const CounterSalePos = React.lazy(
  () => import("../views/app_pages/Sales/CounterSale/components/PosForm"),
);
const SalesOrderEdit = React.lazy(
  () => import("../views/app_pages/Sales/SalesOrder/components/EditComponent"),
);
const Invoice = React.lazy(() => import("../views/app_pages/Sales/Invoice"));
const InvoiceList = React.lazy(
  () => import("../views/app_pages/Sales/Invoice/components/ListComponent"),
);
const CreateInvoiceComponent = React.lazy(
  () =>
    import("../views/app_pages/Sales/Invoice/components/CreateInvoiceComponent"),
);
const EditInvoiceComponent = React.lazy(
  () =>
    import("../views/app_pages/Sales/Invoice/components/EditInvoiceComponent"),
);
const Picklist = React.lazy(
  () => import("../views/app_pages/Warehouse/Picklist"),
);
const PicklistList = React.lazy(
  () =>
    import("../views/app_pages/Warehouse/Picklist/components/ListComponent"),
);
const PicklistAdd = React.lazy(
  () => import("../views/app_pages/Warehouse/Picklist/components/AddComponent"),
);
const PicklistEdit = React.lazy(
  () =>
    import("../views/app_pages/Warehouse/Picklist/components/EditComponent"),
);

const Approver = React.lazy(() => import("../views/app_pages/Approver"));
const Customer = React.lazy(() => import("../views/app_pages/Customer"));
const CustomerList = React.lazy(
  () => import("../views/app_pages/Customer/components/ListComponent"),
);
const CustomerAdd = React.lazy(
  () => import("../views/app_pages/Customer/components/AddComponent"),
);
const CustomerEdit = React.lazy(
  () => import("../views/app_pages/Customer/components/EditComponent"),
);
const Product = React.lazy(() => import("../views/app_pages/Product"));
const ProductAdd = React.lazy(
  () => import("../views/app_pages/Product/components/AddComponent"),
);
const ProductList = React.lazy(
  () => import("../views/app_pages/Product/components/ListComponent"),
);
const ProductEdit = React.lazy(
  () => import("../views/app_pages/Product/components/EditComponent"),
);
const ProductClone = React.lazy(
  () => import("../views/app_pages/Product/components/CloneComponent"),
);

// const CompositeItems = React.lazy(() =>
//   import("../views/app_pages/CompositeItems")
// );
// const CompositeItemsList = React.lazy(() =>
//   import("../views/app_pages/CompositeItems/components/ListComponent")
// );
// const CompositeProductAdd = React.lazy(() =>
//   import("../views/app_pages/CompositeItems/components/AddComponent")
// );
// const CompositeProductEdit = React.lazy(() =>
//   import("../views/app_pages/CompositeItems/components/EditComponent")
// );
// const CompositeProductClone = React.lazy(() =>
//   import("../views/app_pages/CompositeItems/components/CloneComponent")
// );
const Vendor = React.lazy(() => import("../views/app_pages/Vendor"));
const VendorList = React.lazy(
  () => import("../views/app_pages/Vendor/components/ListComponent"),
);
const VendorAdd = React.lazy(
  () => import("../views/app_pages/Vendor/components/AddComponent"),
);
const VendorEdit = React.lazy(
  () => import("../views/app_pages/Vendor/components/EditComponent"),
);
const ProductGroup = React.lazy(
  () => import("../views/app_pages/ProductGroup"),
);
const ProductGroupList = React.lazy(
  () => import("../views/app_pages/ProductGroup/components/ListComponent"),
);
const ProductGroupAdd = React.lazy(
  () => import("../views/app_pages/ProductGroup/components/AddComponent"),
);
const ProductGroupEdit = React.lazy(
  () => import("../views/app_pages/ProductGroup/components/EditComponent"),
);
const ProductAddToGroup = React.lazy(
  () =>
    import("../views/app_pages/ProductGroup/components/DetailComponent/AddItemComponent"),
);
const TenantEmailConfiguration = React.lazy(
  () => import("../views/app_pages/TenantEmailConfiguration"),
);
const TenantEmailTemplate = React.lazy(
  () => import("../views/app_pages/TenantEmailTemplate"),
);
const TenantEmailTemplateList = React.lazy(
  () =>
    import("../views/app_pages/TenantEmailTemplate/components/ListComponent"),
);
const TenantEmailTemplateEdit = React.lazy(
  () =>
    import("../views/app_pages/TenantEmailTemplate/components/EditComponent"),
);
const Manufacturer = React.lazy(
  () => import("../views/app_pages/Manufacturer"),
);
const ManufacturerList = React.lazy(
  () => import("../views/app_pages/Manufacturer/components/ListComponent"),
);
const ManufacturerAdd = React.lazy(
  () => import("../views/app_pages/Manufacturer/components/AddComponent"),
);
const ManufacturerEdit = React.lazy(
  () => import("../views/app_pages/Manufacturer/components/EditComponent"),
);
const Shipment = React.lazy(() => import("../views/app_pages/Shipment"));
const ShipmentList = React.lazy(
  () => import("../views/app_pages/Shipment/components/ListComponent"),
);
const ShipmentAdd = React.lazy(
  () => import("../views/app_pages/Shipment/components/AddComponent"),
);
const ShipmentEdit = React.lazy(
  () => import("../views/app_pages/Shipment/components/EditComponent"),
);
const ShipmentDetailPage = React.lazy(
  () => import("../views/app_pages/Shipment/components/DetailPage"),
);
const DeliveryChallan = React.lazy(
  () => import("../views/app_pages/DeliveryChallan"),
);
const DeliveryChallanList = React.lazy(
  () => import("../views/app_pages/DeliveryChallan/components/ListComponent"),
);
const DeliveryChallanAdd = React.lazy(
  () => import("../views/app_pages/DeliveryChallan/components/AddComponent"),
);
const DeliveryChallanEdit = React.lazy(
  () => import("../views/app_pages/DeliveryChallan/components/EditComponent"),
);
const DeliveryChallanDetailPage = React.lazy(
  () => import("../views/app_pages/DeliveryChallan/components/DetailPage"),
);
const SalesReturn = React.lazy(
  () => import("../views/app_pages/Sales/SalesReturn"),
);
const SalesReturnList = React.lazy(
  () => import("../views/app_pages/Sales/SalesReturn/components/ListComponent"),
);
const SalesReturnAdd = React.lazy(
  () => import("../views/app_pages/Sales/SalesReturn/components/AddComponent"),
);
const SalesReturnEdit = React.lazy(
  () => import("../views/app_pages/Sales/SalesReturn/components/EditComponent"),
);

// Credit Note
const CreditNote = React.lazy(
  () => import("../views/app_pages/Sales/CreditNote"),
);
const CreditNoteList = React.lazy(
  () => import("../views/app_pages/Sales/CreditNote/components/ListComponent"),
);
const CreditNoteCreate = React.lazy(
  () =>
    import("../views/app_pages/Sales/CreditNote/components/CreateCreditNoteComponent"),
);
const CreditNoteEdit = React.lazy(
  () =>
    import("../views/app_pages/Sales/CreditNote/components/EditCreditNoteComponent"),
);

// Item Adjustment
const ItemAdjustment = React.lazy(
  () => import("../views/app_pages/ItemAdjustment"),
);
const ItemAdjustmentList = React.lazy(
  () => import("../views/app_pages/ItemAdjustment/components/ListComponent"),
);
const ItemAdjustmentAdd = React.lazy(
  () => import("../views/app_pages/ItemAdjustment/components/AddComponent"),
);
const ItemAdjustmentEdit = React.lazy(
  () => import("../views/app_pages/ItemAdjustment/components/EditComponent"),
);

// Transfer Order
const TransferOrder = React.lazy(
  () => import("../views/app_pages/TransferOrder"),
);
const TransferOrderList = React.lazy(
  () => import("../views/app_pages/TransferOrder/components/ListComponent"),
);
const TransferOrderAdd = React.lazy(
  () => import("../views/app_pages/TransferOrder/components/AddComponent"),
);
const TransferOrderEdit = React.lazy(
  () => import("../views/app_pages/TransferOrder/components/EditComponent"),
);

// Backorder
const Backorder = React.lazy(
  () => import("../views/app_pages/Warehouse/Backorder"),
);
const BackorderList = React.lazy(
  () =>
    import("../views/app_pages/Warehouse/Backorder/components/ListComponent"),
);
const BackorderAdd = React.lazy(
  () =>
    import("../views/app_pages/Warehouse/Backorder/components/AddComponent"),
);
const BackorderEdit = React.lazy(
  () =>
    import("../views/app_pages/Warehouse/Backorder/components/EditComponent"),
);
const FulfillBackorders = React.lazy(
  () => import("../views/app_pages/Warehouse/Backorder/FulfillBackorders"),
);

/*---------Admin Component----------*/
const AdminLogin = React.lazy(() => import("../views/admin_pages/Auth/Login"));
const Tenant = React.lazy(() => import("../views/admin_pages/Tenant"));
const TenantList = React.lazy(
  () => import("../views/admin_pages/Tenant/components/ListComponent"),
);
const TenantAdd = React.lazy(
  () => import("../views/admin_pages/Tenant/components/AddComponent"),
);
const TenantEdit = React.lazy(
  () => import("../views/admin_pages/Tenant/components/EditComponent"),
);
const EmailConfiguration = React.lazy(
  () => import("../views/admin_pages/Tenant/components/ProviderConfiguration"),
);
const EInvoiceConfiguration = React.lazy(
  () => import("../views/admin_pages/Tenant/components/EInvoiceConfiguration"),
);
const EmailTemplate = React.lazy(
  () => import("../views/admin_pages/EmailTemplate"),
);
const EmailTemplateList = React.lazy(
  () => import("../views/admin_pages/EmailTemplate/components/ListComponent"),
);
const EmailTemplateAdd = React.lazy(
  () => import("../views/admin_pages/EmailTemplate/components/AddComponent"),
);
const EmailTemplateEdit = React.lazy(
  () => import("../views/admin_pages/EmailTemplate/components/EditComponent"),
);
const TenantInquiry = React.lazy(
  () => import("../views/admin_pages/TenantInquiry"),
);
const TenantInquiryList = React.lazy(
  () => import("../views/admin_pages/TenantInquiry/components/ListComponent"),
);
const TenantInquiryAdd = React.lazy(
  () => import("../views/admin_pages/TenantInquiry/components/AddComponent"),
);
const TenantInquiryAddExternal = React.lazy(
  () =>
    import("../views/admin_pages/TenantInquiry/components/AddTenantInquiry"),
);
const TenantInquiryEdit = React.lazy(
  () => import("../views/admin_pages/TenantInquiry/components/EditComponent"),
);
const Territory = React.lazy(() => import("../views/app_pages/Territory"));
const FiscalCalendar = React.lazy(
  () => import("../views/app_pages/FiscalCalendar"),
);
const Onboarding = React.lazy(() => import("../views/app_pages/Onboarding"));
const Package = React.lazy(() => import("../views/app_pages/Package"));
const PackageList = React.lazy(
  () => import("../views/app_pages/Package/components/ListComponent"),
);
const CreatePackagePage = React.lazy(
  () => import("../views/app_pages/Package/components/CreatePackagePage"),
);
const PackageWorkspacePage = React.lazy(
  () => import("../views/app_pages/Package/components/PackageWorkspacePage"),
);
const PackageDetailPage = React.lazy(
  () => import("../views/app_pages/Package/components/DetailPage"),
);
const EmailProvider = React.lazy(
  () => import("../views/admin_pages/EmailProvider"),
);

// Hidden super-admin-only data-repair page. NOT in CONSTANT on purpose — accessed
// by typing the URL (/admin/repair-invoice-posting). See
// Goods-Receipt-Import-API/KnowledgeBase/Runbook-Repair-Invoice-Posting-SideEffects.md
const InvoiceRepair = React.lazy(
  () => import("../views/admin_pages/InvoiceRepair"),
);

// Reports
const ReportsCenter = React.lazy(
  () => import("../views/app_pages/Reports/ReportsCenter"),
);
const StockSummaryReport = React.lazy(
  () => import("../views/app_pages/Reports/StockReports/StockSummaryReport"),
);
const StockValuationReport = React.lazy(
  () => import("../views/app_pages/Reports/StockReports/StockValuationReport"),
);
const LowStockReport = React.lazy(
  () => import("../views/app_pages/Reports/StockReports/LowStockReport"),
);
const StockAgingReport = React.lazy(
  () => import("../views/app_pages/Reports/StockReports/StockAgingReport"),
);
const BinStockReport = React.lazy(
  () => import("../views/app_pages/Reports/StockReports/BinStockReport"),
);
const StockAdjustmentReport = React.lazy(
  () => import("../views/app_pages/Reports/StockReports/StockAdjustmentReport"),
);
const StockLedger = React.lazy(
  () => import("../views/app_pages/Reports/StockReports/StockLedger"),
);
const PurchaseOrderReport = React.lazy(
  () =>
    import("../views/app_pages/Reports/PurchaseReports/PurchaseOrderReport"),
);
const PendingPOReport = React.lazy(
  () => import("../views/app_pages/Reports/PurchaseReports/PendingPOReport"),
);
const GoodsReceiptReport = React.lazy(
  () => import("../views/app_pages/Reports/PurchaseReports/GoodsReceiptReport"),
);
const PurchaseVarianceReport = React.lazy(
  () =>
    import("../views/app_pages/Reports/PurchaseReports/PurchaseVarianceReport"),
);
const PurchaseRegisterReport = React.lazy(
  () =>
    import("../views/app_pages/Reports/PurchaseReports/PurchaseRegisterReport"),
);
const PurchaseMonthlyReport = React.lazy(
  () =>
    import("../views/app_pages/Reports/PurchaseReports/PurchaseMonthlyReport"),
);
const SalesOrderReport = React.lazy(
  () => import("../views/app_pages/Reports/SalesReports/SalesOrderReport"),
);
const PendingSOReport = React.lazy(
  () => import("../views/app_pages/Reports/SalesReports/PendingSOReport"),
);
const BackorderReport = React.lazy(
  () => import("../views/app_pages/Reports/SalesReports/BackorderReport"),
);
const SalesByCustomerReport = React.lazy(
  () => import("../views/app_pages/Reports/SalesReports/SalesByCustomerReport"),
);
const SalesByItemReport = React.lazy(
  () => import("../views/app_pages/Reports/SalesReports/SalesByItemReport"),
);
const SalesByCategoryReport = React.lazy(
  () => import("../views/app_pages/Reports/SalesReports/SalesByCategoryReport"),
);
const SalesByBrandReport = React.lazy(
  () => import("../views/app_pages/Reports/SalesReports/SalesByBrandReport"),
);
const SalesByInvoiceReport = React.lazy(
  () => import("../views/app_pages/Reports/SalesReports/SalesByInvoiceReport"),
);
const SalesBySalesPersonReport = React.lazy(
  () => import("../views/app_pages/Reports/SalesReports/SalesBySalesPersonReport"),
);
const CustomerItemSalesReport = React.lazy(
  () =>
    import("../views/app_pages/Reports/SalesReports/CustomerItemSalesReport"),
);
const CustomerMonthlySalesReport = React.lazy(
  () =>
    import("../views/app_pages/Reports/SalesReports/CustomerMonthlySalesReport"),
);
const NetSalesRegisterReport = React.lazy(
  () =>
    import("../views/app_pages/Reports/SalesReports/NetSalesRegisterReport/index"),
);
const BusinessSalesTransactionReport = React.lazy(
  () =>
    import("../views/app_pages/Reports/SalesReports/BusinessSalesTransactionReport/index"),
);
const SalesVsFulfillmentReport = React.lazy(
  () =>
    import("../views/app_pages/Reports/SalesReports/SalesVsFulfillmentReport"),
);
const ItemWiseSalesReturnReport = React.lazy(
  () =>
    import("../views/app_pages/Reports/SalesReports/SalesReturnAndRMAReport"),
);
const Gstr1Report = React.lazy(
  () => import("../views/app_pages/Reports/GSTRReports/GSTR1Report"),
);
const CounterSalesReport = React.lazy(
  () =>
    import("../views/app_pages/Reports/SalesReports/CounterSalesReport"),
);
const InvoiceReportPage = React.lazy(
  () => import("../views/app_pages/Reports/InvoiceReports/InvoiceReport"),
);
const OutstandingInvoiceReport = React.lazy(
  () =>
    import("../views/app_pages/Reports/InvoiceReports/OutstandingInvoiceReport"),
);
const InvoiceAgingReport = React.lazy(
  () => import("../views/app_pages/Reports/InvoiceReports/InvoiceAgingReport"),
);
const ExportHistory = React.lazy(
  () => import("../views/app_pages/Reports/ExportHistory"),
);

// Import Template
const ImportTemplate = React.lazy(
  () => import("../views/app_pages/ImportTemplate"),
);
const ImportTemplateList = React.lazy(
  () => import("../views/app_pages/ImportTemplate/components/ListComponent"),
);
const ImportTemplateAdd = React.lazy(
  () => import("../views/app_pages/ImportTemplate/components/AddComponent"),
);
const ImportTemplateEdit = React.lazy(
  () => import("../views/app_pages/ImportTemplate/components/EditComponent"),
);

export default function Router() {
  const element = useRoutes(RouterConfig);
  return element;
}

export const RouterConfig = [
  {
    element: <AuthLayout />,
    children: [
      {
        path: CONSTANT.LOGIN_REDIRECT_PATH,
        element: <Login />,
      },
      {
        path: CONSTANT.ROUTE.REGISTER,
        element: <Registration />,
      },
      {
        path: CONSTANT.ROUTE.FORGOT_PASSWORD,
        element: <ForgotPassword />,
      },
      // {
      // 	path: "/unauthorized",
      // 	element: <Unauthorized />,
      // },
      {
        path: CONSTANT.ADMIN_ROUTE.LOGIN,
        element: <AdminLogin />,
      },
      {
        path: CONSTANT.ADMIN_ROUTE.ADD_TENANT_INQUIRY_EXTERNAL,
        element: <TenantInquiryAddExternal />,
      },
      {
        path: "*",
        element: <PageNotFound />,
      },
    ],
  },
  {
    element: <MainLayout />,
    children: [
      {
        path: CONSTANT.ROUTE.SETTINGS,
        element: <MyProfile />,
      },
      {
        path: CONSTANT.ROUTE.ONBOARDING,
        element: <Onboarding />,
      },
      {
        path: CONSTANT.ROUTE.USER,
        element: <User />,
      },
      {
        path: CONSTANT.ROUTE.ROLE,
        element: <Role />,
        children: [
          {
            index: true,
            element: <RoleList />,
          },
          {
            path: CONSTANT.ROUTE.ADD_ROLE,
            element: <RoleAdd />,
          },
          {
            path: CONSTANT.ROUTE.EDIT_ROLE,
            element: <RoleEdit />,
          },
          {
            path: CONSTANT.ROUTE.ROLE_DETAIL,
            element: <RoleList />,
          },
        ],
      },
      {
        path: CONSTANT.ROUTE.DASHBOARD,
        element: <Dashboard />,
      },
      {
        path: CONSTANT.ROUTE.BRAND,
        element: <Brand />,
      },
      {
        path: CONSTANT.ROUTE.TRANSPORTER,
        element: <Transporter />,
      },
      {
        path: CONSTANT.ROUTE.DISCOUNT_SCHEME,
        element: <DiscountScheme />,
        children: [
          {
            index: true,
            element: <DiscountSchemeList />,
          },
          {
            path: CONSTANT.ROUTE.ADD_DISCOUNT_SCHEME,
            element: <DiscountSchemeAdd />,
          },
          {
            path: CONSTANT.ROUTE.EDIT_DISCOUNT_SCHEME,
            element: <DiscountSchemeEdit />,
          },
        ],
      },
      {
        path: CONSTANT.ROUTE.UNIT_OF_MEASUREMENT,
        element: <UnitOfMeasurement />,
      },
      {
        path: CONSTANT.ROUTE.UQC_MASTER,
        element: <UQCMaster />,
      },
      {
        path: CONSTANT.ROUTE.ITEM_DEFAULT_BIN,
        element: <ItemDefaultBin />,
      },
      // {
      //   path: CONSTANT.ROUTE.WAREHOUSE,
      //   element: <Warehouse />,
      // },
      {
        path: CONSTANT.ROUTE.LOCATION,
        element: <Location />,
      },
      {
        path: CONSTANT.ROUTE.VENDOR,
        element: <Vendor />,
        children: [
          {
            index: true,
            element: <VendorList />,
          },
          {
            path: CONSTANT.ROUTE.ADD_VENDOR,
            element: <VendorAdd />,
          },
          {
            path: CONSTANT.ROUTE.EDIT_VENDOR,
            element: <VendorEdit />,
          },
          {
            path: ":id",
            element: <VendorList />,
          },
        ],
      },
      {
        path: CONSTANT.ROUTE.PRODUCT,
        element: <Product />,
        children: [
          {
            index: true,
            element: <ProductList />,
          },
          {
            path: CONSTANT.ROUTE.ADD_PRODUCT,
            element: <ProductAdd />,
          },
          {
            path: CONSTANT.ROUTE.EDIT_PRODUCT,
            element: <ProductEdit />,
          },
          {
            path: CONSTANT.ROUTE.CLONE_PRODUCT,
            element: <ProductClone />,
          },
          {
            path: CONSTANT.ROUTE.PRODUCT_DETAIL,
            element: <ProductList />,
          },
        ],
      },
      {
        path: CONSTANT.ROUTE.PRODUCT_GROUP,
        element: <ProductGroup />,
        children: [
          {
            index: true,
            element: <ProductGroupList />,
          },
          {
            path: CONSTANT.ROUTE.ADD_PRODUCT_GROUP,
            element: <ProductGroupAdd />,
          },
          {
            path: CONSTANT.ROUTE.EDIT_PRODUCT_GROUP,
            element: <ProductGroupEdit />,
          },
          {
            path: CONSTANT.ROUTE.ADD_PRODUCT_TO_GROUP,
            element: <ProductAddToGroup />,
          },
          {
            path: CONSTANT.ROUTE.PRODUCT_GROUP_DETAIL,
            element: <ProductGroupList />,
          },
        ],
      },
      // {
      //   path: CONSTANT.ROUTE.COMPOSITE_ITEMS,
      //   element: <CompositeItems />,
      //   children: [
      //     {
      //       index: true,
      //       element: <CompositeItemsList />,
      //     },
      //     {
      //       path: CONSTANT.ROUTE.ADD_COMPOSITE_ITEMS,
      //       element: <CompositeProductAdd />,
      //     },
      //     {
      //       path: CONSTANT.ROUTE.EDIT_COMPOSITE_ITEMS,
      //       element: <CompositeProductEdit />,
      //     },
      //     {
      //       path: CONSTANT.ROUTE.CLONE_COMPOSITE_ITEMS,
      //       element: <CompositeProductClone />,
      //     },
      //   ],
      // },
      {
        path: CONSTANT.ROUTE.ITEM_CATEGORY,
        element: <ItemCategory />,
      },
      {
        path: CONSTANT.ROUTE.CUSTOMER_CATEGORY,
        element: <CustomerCategory />,
      },
      {
        path: CONSTANT.ROUTE.CUSTOMER_ACCOUNT_TYPE,
        element: <CustomerAccountType />,
      },
      {
        path: CONSTANT.ROUTE.ITEM_SEGMENT,
        element: <ItemSegment />,
      },
      {
        path: CONSTANT.ROUTE.PRODUCT_GROUP_MASTER,
        element: <ProductGroupMaster />,
      },
      {
        path: CONSTANT.ROUTE.CURRENCY,
        element: <Currency />,
      },
      {
        path: `${CONSTANT.ROUTE.PURCHASE_ORDER}`,
        element: <PurchaseOrder />,
        children: [
          {
            index: true,
            element: <PurchaseOrderList />,
          },
          {
            path: CONSTANT.ROUTE.ADD_PURCHASE_ORDER,
            element: <PurchaseOrderAdd />,
          },
          {
            path: CONSTANT.ROUTE.EDIT_PURCHASE_ORDER,
            element: <PurchaseOrderEdit />,
          },
          {
            path: ":id",
            element: <PurchaseOrderList />,
          },
        ],
      },
      {
        path: CONSTANT.ROUTE.PURCHASE_RECEIVE,
        element: <PurchaseReceive />,
        children: [
          {
            index: true,
            element: <PurchaseReceiveList />,
          },
          {
            path: CONSTANT.ROUTE.ADD_PURCHASE_RECEIVE,
            element: <PurchaseReceiveAdd />,
          },
          {
            path: CONSTANT.ROUTE.ADD_PURCHASE_RECEIVE_FROM_PO,
            element: <PurchaseReceiveAdd />,
          },
          {
            path: CONSTANT.ROUTE.PURCHASE_RECEIVE_DETAIL,
            element: <PurchaseReceiveList />,
          },
          {
            path: CONSTANT.ROUTE.QR_VERIFICATION,
            element: <QRVerificationComponent />,
          },
          {
            path: CONSTANT.ROUTE.PURCHASE_RECEIVE_RECEIVE,
            element: <PurchaseReceiveReceive />,
          },
          {
            path: CONSTANT.ROUTE.INVOICE_RECEIVE,
            element: <PurchaseInvoiceReceive />,
          },
          {
            path: CONSTANT.ROUTE.INVOICE_RECEIVING,
            element: <PurchaseInvoiceReceiving />,
          },
          {
            path: CONSTANT.ROUTE.INVOICE_QR_VERIFICATION,
            element: <PurchaseInvoiceVerification />,
          },
          {
            path: CONSTANT.ROUTE.RECEIVE_SESSION_START,
            element: <ReceiveSessionStart />,
          },
          {
            path: CONSTANT.ROUTE.RECEIVE_SESSION_SCAN,
            element: <ReceiveSessionScan />,
          },
          {
            path: CONSTANT.ROUTE.RECEIVE_SESSION_FINALIZE,
            element: <ReceiveSessionFinalize />,
          },
          {
            path: ":id",
            element: <PurchaseReceiveList />,
          },
          {
            path: CONSTANT.ROUTE.PURCHASE_RECEIVE_INVOICE,
            element: <PurchaseReceiveList />,
            children: [
              {
                path: ":invoiceNumber",
                element: <PurchaseReceiveList />,
              },
            ],
          },
          {
            path: CONSTANT.ROUTE.PURCHASE_RECEIVE_BATCH,
            element: <PurchaseReceiveList />,
          },
        ],
      },
      ...(CONSTANT.FEATURE_FLAGS.QUOTATION_ENABLED
        ? [
            {
              path: CONSTANT.ROUTE.QUOTATION,
              element: <Quotation />,
              children: [
                {
                  index: true,
                  element: <QuotationList />,
                },
                {
                  path: CONSTANT.ROUTE.ADD_QUOTATION,
                  element: <QuotationAdd />,
                },
                {
                  path: CONSTANT.ROUTE.EDIT_QUOTATION,
                  element: <QuotationEdit />,
                },
                {
                  path: CONSTANT.ROUTE.QUOTATION_DETAIL,
                  element: <QuotationList />,
                },
              ],
            },
          ]
        : []),
      {
        path: CONSTANT.ROUTE.SALES_ORDER,
        element: <SalesOrder />,
        children: [
          {
            index: true,
            element: <SalesOrderList />,
          },
          {
            path: CONSTANT.ROUTE.ADD_SALES_ORDER,
            element: <SalesOrderAdd />,
          },
          {
            path: CONSTANT.ROUTE.EDIT_SALES_ORDER,
            element: <SalesOrderEdit />,
          },
          {
            path: CONSTANT.ROUTE.SALES_ORDER_DETAIL,
            element: <SalesOrderList />,
          },
        ],
      },
      {
        path: CONSTANT.ROUTE.COUNTER_SALE,
        element: <CounterSale />,
        children: [
          {
            index: true,
            element: <CounterSaleList />,
          },
          {
            path: CONSTANT.ROUTE.ADD_COUNTER_SALE,
            element: <CounterSaleAdd />,
          },
          {
            path: CONSTANT.ROUTE.COUNTER_SALE_POS,
            element: <CounterSalePos />,
          },
          {
            // POS-style edit. Reuses the same component as the
            // create flow; the form switches into "edit mode" when
            // the :id segment is present and applies status-based
            // field locks (full edit for Draft, restricted for
            // Posted).
            path: CONSTANT.ROUTE.COUNTER_SALE_POS_EDIT,
            element: <CounterSalePos />,
          },
          {
            path: CONSTANT.ROUTE.EDIT_COUNTER_SALE,
            element: <CounterSaleEdit />,
          },
          {
            path: CONSTANT.ROUTE.COUNTER_SALE_DETAIL,
            element: <CounterSaleList />,
          },
        ],
      },
      {
        path: CONSTANT.ROUTE.INVOICE,
        element: <Invoice />,
        children: [
          {
            index: true,
            element: <InvoiceList />,
          },
          {
            path: CONSTANT.ROUTE.CREATE_INVOICE,
            element: <CreateInvoiceComponent />,
          },
          {
            path: CONSTANT.ROUTE.EDIT_INVOICE,
            element: <EditInvoiceComponent />,
          },
          {
            path: "pending",
            element: <InvoiceList />,
          },
          {
            path: CONSTANT.ROUTE.INVOICE_DETAIL,
            element: <InvoiceList />,
          },
        ],
      },
      {
        path: CONSTANT.ROUTE.SALES_RETURN,
        element: <SalesReturn />,
        children: [
          {
            index: true,
            element: <SalesReturnList />,
          },
          {
            path: CONSTANT.ROUTE.ADD_SALES_RETURN,
            element: <SalesReturnAdd />,
          },
          {
            path: CONSTANT.ROUTE.EDIT_SALES_RETURN,
            element: <SalesReturnEdit />,
          },
          {
            path: CONSTANT.ROUTE.SALES_RETURN_DETAIL,
            element: <SalesReturnList />,
          },
        ],
      },
      {
        path: CONSTANT.ROUTE.CREDIT_NOTE,
        element: <CreditNote />,
        children: [
          {
            index: true,
            element: <CreditNoteList />,
          },
          {
            path: CONSTANT.ROUTE.CREATE_CREDIT_NOTE,
            element: <CreditNoteCreate />,
          },
          {
            path: CONSTANT.ROUTE.EDIT_CREDIT_NOTE,
            element: <CreditNoteEdit />,
          },
          {
            path: CONSTANT.ROUTE.CREDIT_NOTE_DETAIL,
            element: <CreditNoteList />, // Will be replaced with DetailPage when created
          },
        ],
      },
      {
        path: CONSTANT.ROUTE.ITEM_ADJUSTMENT,
        element: <ItemAdjustment />,
        children: [
          {
            index: true,
            element: <ItemAdjustmentList />,
          },
          {
            path: CONSTANT.ROUTE.ADD_ITEM_ADJUSTMENT,
            element: <ItemAdjustmentAdd />,
          },
          {
            path: CONSTANT.ROUTE.EDIT_ITEM_ADJUSTMENT,
            element: <ItemAdjustmentEdit />,
          },
          {
            path: CONSTANT.ROUTE.ITEM_ADJUSTMENT_DETAIL,
            element: <ItemAdjustmentList />,
          },
        ],
      },
      {
        path: CONSTANT.ROUTE.TRANSFER_ORDER,
        element: <TransferOrder />,
        children: [
          {
            index: true,
            element: <TransferOrderList />,
          },
          {
            path: CONSTANT.ROUTE.ADD_TRANSFER_ORDER,
            element: <TransferOrderAdd />,
          },
          {
            path: CONSTANT.ROUTE.EDIT_TRANSFER_ORDER,
            element: <TransferOrderEdit />,
          },
          {
            path: CONSTANT.ROUTE.TRANSFER_ORDER_DETAIL,
            element: <TransferOrderList />,
          },
        ],
      },
      {
        path: CONSTANT.ROUTE.STOCK_LEDGER,
        element: <StockLedger />,
      },
      {
        path: `${CONSTANT.ROUTE.PICKLIST}`,
        element: <Picklist />,
        children: [
          {
            index: true,
            element: <PicklistList />,
          },
          {
            path: CONSTANT.ROUTE.ADD_PICKLIST,
            element: <PicklistAdd />,
          },
          {
            path: CONSTANT.ROUTE.ADD_PICKLIST_FROM_SO,
            element: <PicklistAdd />,
          },
          {
            path: CONSTANT.ROUTE.EDIT_PICKLIST,
            element: <PicklistEdit />,
          },
          {
            path: "pending",
            element: <PicklistList />,
          },
          {
            path: ":id",
            element: <PicklistList />,
          },
        ],
      },
      {
        path: CONSTANT.ROUTE.BACKORDER,
        element: <Backorder />,
        children: [
          {
            index: true,
            element: <BackorderList />,
          },
          {
            path: CONSTANT.ROUTE.ADD_BACKORDER,
            element: <BackorderAdd />,
          },
          {
            path: CONSTANT.ROUTE.ADD_BACKORDER_FROM_SO,
            element: <BackorderAdd />,
          },
          {
            path: CONSTANT.ROUTE.EDIT_BACKORDER,
            element: <BackorderEdit />,
          },
          {
            path: CONSTANT.ROUTE.FULFILL_BACKORDERS,
            element: <FulfillBackorders />,
          },
          {
            path: CONSTANT.ROUTE.BACKORDER_DETAIL,
            element: <BackorderList />,
          },
        ],
      },
      {
        path: CONSTANT.ROUTE.CUSTOMER,
        element: <Customer />,
        children: [
          {
            index: true,
            element: <CustomerList />,
          },
          {
            path: CONSTANT.ROUTE.ADD_CUSTOMER,
            element: <CustomerAdd />,
          },
          {
            path: CONSTANT.ROUTE.EDIT_CUSTOMER,
            element: <CustomerEdit />,
          },
          {
            path: ":id",
            element: <CustomerList />,
          },
        ],
      },
      {
        path: CONSTANT.ROUTE.APPROVER,
        element: <Approver />,
      },
      {
        path: CONSTANT.ADMIN_ROUTE.TENANT,
        element: <Tenant />,
        children: [
          {
            index: true,
            element: <TenantList />,
          },
          {
            path: CONSTANT.ADMIN_ROUTE.ADD_TENANT,
            element: <TenantAdd />,
          },
          {
            path: CONSTANT.ADMIN_ROUTE.EDIT_TENANT,
            element: <TenantEdit />,
          },
          {
            path: CONSTANT.ADMIN_ROUTE.EMAIL_CONFIGURATION,
            element: <EmailConfiguration />,
          },
          {
            path: CONSTANT.ADMIN_ROUTE.EINVOICE_CONFIGURATION,
            element: <EInvoiceConfiguration />,
          },
          {
            path: CONSTANT.ADMIN_ROUTE.TENANT_DETAIL,
            element: <TenantList />,
          },
        ],
      },
      {
        path: CONSTANT.ADMIN_ROUTE.TENANT_INQUIRY,
        element: <TenantInquiry />,
        children: [
          {
            index: true,
            element: <TenantInquiryList />,
          },
          {
            path: CONSTANT.ADMIN_ROUTE.ADD_TENANT_INQUIRY,
            element: <TenantInquiryAdd />,
          },
          {
            path: CONSTANT.ADMIN_ROUTE.EDIT_TENANT_INQUIRY,
            element: <TenantInquiryEdit />,
          },
          {
            path: CONSTANT.ADMIN_ROUTE.TENANT_INQUIRY_DETAIL,
            element: <TenantInquiryList />,
          },
        ],
      },
      {
        path: CONSTANT.ROUTE.TERRITORY,
        element: <Territory />,
      },
      {
        path: CONSTANT.ADMIN_ROUTE.EMAIL_PROVIDER,
        element: <EmailProvider />,
      },
      // Hidden super-admin-only data-repair route (not in CONSTANT, not in any menu)
      {
        path: "admin/repair-invoice-posting",
        element: <InvoiceRepair />,
      },
      {
        path: CONSTANT.ADMIN_ROUTE.EMAIL_TEMPLATE,
        element: <EmailTemplate />,
        children: [
          {
            index: true,
            element: <EmailTemplateList />,
          },
          {
            path: CONSTANT.ADMIN_ROUTE.ADD_EMAIL_TEMPLATE,
            element: <EmailTemplateAdd />,
          },
          {
            path: CONSTANT.ADMIN_ROUTE.EDIT_EMAIL_TEMPLATE,
            element: <EmailTemplateEdit />,
          },
          {
            path: ":id",
            element: <EmailTemplateList />,
          },
        ],
      },
      {
        path: CONSTANT.ROUTE.TENANT_EMAIL_CONFIGURATION,
        element: <TenantEmailConfiguration />,
      },
      {
        path: CONSTANT.ROUTE.TENANT_EMAIL_TEMPLATE,
        element: <TenantEmailTemplate />,
        children: [
          {
            index: true,
            element: <TenantEmailTemplateList />,
          },
          {
            path: CONSTANT.ROUTE.EDIT_TENANT_EMAIL_TEMPLATE,
            element: <TenantEmailTemplateEdit />,
          },
        ],
      },
      {
        path: CONSTANT.ROUTE.MANUFACTURER,
        element: <Manufacturer />,
        children: [
          {
            index: true,
            element: <ManufacturerList />,
          },
          {
            path: CONSTANT.ROUTE.ADD_MANUFACTURER,
            element: <ManufacturerAdd />,
          },
          {
            path: CONSTANT.ROUTE.EDIT_MANUFACTURER,
            element: <ManufacturerEdit />,
          },
        ],
      },
      {
        path: CONSTANT.ROUTE.SHIPMENT,
        element: <Shipment />,
        children: [
          {
            index: true,
            element: <ShipmentList />,
          },
          {
            path: CONSTANT.ROUTE.ADD_SHIPMENT,
            element: <ShipmentAdd />,
          },
          {
            path: CONSTANT.ROUTE.EDIT_SHIPMENT,
            element: <ShipmentEdit />,
          },
          {
            path: "pending",
            element: <ShipmentList />,
          },
          {
            path: ":id",
            element: <ShipmentList />,
          },
        ],
      },
      {
        path: CONSTANT.ROUTE.DELIVERY_CHALLAN,
        element: <DeliveryChallan />,
        children: [
          {
            index: true,
            element: <DeliveryChallanList />,
          },
          {
            path: CONSTANT.ROUTE.ADD_DELIVERY_CHALLAN,
            element: <DeliveryChallanAdd />,
          },
          {
            path: CONSTANT.ROUTE.EDIT_DELIVERY_CHALLAN,
            element: <DeliveryChallanEdit />,
          },
          {
            path: "pending",
            element: <DeliveryChallanList />,
          },
          {
            path: CONSTANT.ROUTE.DELIVERY_CHALLAN_DETAIL,
            element: <DeliveryChallanList />,
          },
        ],
      },
      {
        path: CONSTANT.ROUTE.PACKAGES,
        element: <Package />,
        children: [
          {
            index: true,
            element: <PackageList />,
          },
          {
            // Create route MUST come before :packageId to avoid "create" being treated as packageId
            path: CONSTANT.ROUTE.CREATE_PACKAGE,
            element: <CreatePackagePage />,
          },
          {
            // Detail route for full-page view (ctrl+click from EntityLink)
            // Must come BEFORE :packageId to take precedence
            path: ":id/view",
            element: <PackageDetailPage />,
          },
          {
            // Workspace route for editing packages
            path: CONSTANT.ROUTE.PACKAGE_WORKSPACE,
            element: <PackageWorkspacePage />,
          },
          {
            path: "pending",
            element: <PackageList />,
          },
          {
            path: CONSTANT.ROUTE.PACKAGE_DETAIL,
            element: <PackageList />,
          },
        ],
      },
      {
        path: CONSTANT.ROUTE.FISCAL_CALENDAR,
        element: <FiscalCalendar />,
      },
      // Reports
      {
        path: CONSTANT.ROUTE.REPORTS,
        element: <ReportsCenter />,
      },
      {
        path: CONSTANT.ROUTE.STOCK_SUMMARY_REPORT,
        element: <StockSummaryReport />,
      },
      {
        path: CONSTANT.ROUTE.STOCK_VALUATION_REPORT,
        element: <StockValuationReport />,
      },
      {
        path: CONSTANT.ROUTE.LOW_STOCK_REPORT,
        element: <LowStockReport />,
      },
      {
        path: CONSTANT.ROUTE.STOCK_AGING_REPORT,
        element: <StockAgingReport />,
      },
      {
        path: CONSTANT.ROUTE.BIN_STOCK_REPORT,
        element: <BinStockReport />,
      },
      {
        path: CONSTANT.ROUTE.STOCK_ADJUSTMENT_REPORT,
        element: <StockAdjustmentReport />,
      },
      {
        path: CONSTANT.ROUTE.PURCHASE_ORDER_REPORT,
        element: <PurchaseOrderReport />,
      },
      {
        path: CONSTANT.ROUTE.PENDING_PO_REPORT,
        element: <PendingPOReport />,
      },
      {
        path: CONSTANT.ROUTE.GOODS_RECEIPT_REPORT,
        element: <GoodsReceiptReport />,
      },
      {
        path: CONSTANT.ROUTE.PURCHASE_VARIANCE_REPORT,
        element: <PurchaseVarianceReport />,
      },
      {
        path: CONSTANT.ROUTE.PURCHASE_REGISTER_REPORT,
        element: <PurchaseRegisterReport />,
      },
      {
        path: CONSTANT.ROUTE.PURCHASE_MONTHLY_REPORT,
        element: <PurchaseMonthlyReport />,
      },
      {
        path: CONSTANT.ROUTE.SALES_ORDER_REPORT,
        element: <SalesOrderReport />,
      },
      {
        path: CONSTANT.ROUTE.PENDING_SO_REPORT,
        element: <PendingSOReport />,
      },
      {
        path: CONSTANT.ROUTE.BACKORDER_REPORT,
        element: <BackorderReport />,
      },
      {
        path: CONSTANT.ROUTE.SALES_BY_CUSTOMER_REPORT,
        element: <SalesByCustomerReport />,
      },
      {
        path: CONSTANT.ROUTE.SALES_BY_ITEM_REPORT,
        element: <SalesByItemReport />,
      },
      {
        path: CONSTANT.ROUTE.SALES_BY_CATEGORY_REPORT,
        element: <SalesByCategoryReport />,
      },
      {
        path: CONSTANT.ROUTE.SALES_BY_BRAND_REPORT,
        element: <SalesByBrandReport />,
      },
      {
        path: CONSTANT.ROUTE.SALES_BY_INVOICE_REPORT,
        element: <SalesByInvoiceReport />,
      },
      {
        path: CONSTANT.ROUTE.SALES_BY_SALES_PERSON_REPORT,
        element: <SalesBySalesPersonReport />,
      },
      {
        path: CONSTANT.ROUTE.CUSTOMER_ITEM_SALES_REPORT,
        element: <CustomerItemSalesReport />,
      },
      {
        path: CONSTANT.ROUTE.CUSTOMER_MONTHLY_SALES_REPORT,
        element: <CustomerMonthlySalesReport />,
      },
      {
        path: CONSTANT.ROUTE.NET_SALES_REGISTER_REPORT,
        element: <NetSalesRegisterReport />,
      },
      {
        path: CONSTANT.ROUTE.BUSINESS_SALES_TRANSACTION_REPORT,
        element: <BusinessSalesTransactionReport />,
      },
      {
        path: CONSTANT.ROUTE.SALES_VS_FULFILLMENT_REPORT,
        element: <SalesVsFulfillmentReport />,
      },
      {
        path: CONSTANT.ROUTE.ITEM_WISE_SALES_RETURN_REPORT,
        element: <ItemWiseSalesReturnReport />,
      },
      {
        path: CONSTANT.ROUTE.GSTR1_REPORT,
        element: <Gstr1Report />,
      },
      {
        path: CONSTANT.ROUTE.COUNTER_SALES_REPORT,
        element: <CounterSalesReport />,
      },
      {
        path: CONSTANT.ROUTE.INVOICE_REPORT,
        element: <InvoiceReportPage />,
      },
      {
        path: CONSTANT.ROUTE.OUTSTANDING_INVOICE_REPORT,
        element: <OutstandingInvoiceReport />,
      },
      {
        path: CONSTANT.ROUTE.INVOICE_AGING_REPORT,
        element: <InvoiceAgingReport />,
      },
      {
        path: CONSTANT.ROUTE.EXPORT_HISTORY,
        element: <ExportHistory />,
      },
      {
        path: CONSTANT.ROUTE.IMPORT_TEMPLATE,
        element: <ImportTemplate />,
        children: [
          {
            index: true,
            element: <ImportTemplateList />,
          },
          {
            path: CONSTANT.ROUTE.ADD_IMPORT_TEMPLATE,
            element: <ImportTemplateAdd />,
          },
          {
            path: CONSTANT.ROUTE.EDIT_IMPORT_TEMPLATE,
            element: <ImportTemplateEdit />,
          },
        ],
      },
      {
        path: "*",
        element: <PageNotFound />,
      },
    ],
  },
];
